import React, { useEffect, useState, useCallback, useRef } from 'react'
import { Unity, useUnityContext } from 'react-unity-webgl-latest'
import jsPDF from 'jspdf'
import { Modal, Button, Spin, Icon, message } from 'antd'
import {
  DownloadOutlined,
  LoadingOutlined,
  SaveOutlined
} from '@ant-design/icons'
import { config } from '../../../../constants'
import { useTranslation } from 'react-i18next'
import history from '../../../../history'
import { getToken } from '../../../../services/api/user'
import { generateMazeSummary } from '../../../../services/api/maze'

import './UnityView.scss'
import { useDispatch, useSelector } from 'react-redux'
import actions from '../../../../store/actions'

function UnityView(props) {
  const {
    unityProvider,
    addEventListener,
    removeEventListener,
    sendMessage,
    isLoaded,
    username,
    unload,
    loadingProgression,
    error: unityError
  } = useUnityContext({
    loaderUrl: '/uBuild/Build.loader.js',
    dataUrl: '/uBuild/Build.data',
    frameworkUrl: '/uBuild/Build.framework.js',
    codeUrl: '/uBuild/Build.wasm'
  })

  const newResponseMockData = {
    overview:
      'The assessment aimed to evaluate your understanding of Large Language Models (LLMs) and their significance in AI development. The end goal was to assess your knowledge of "Großes Sprachmodell" (Large Language Model in German), focusing on prominent LLM families like GPT, LLaMA, and PaLM, and their role in advancing toward artificial general intelligence (AGI). The examiner wanted to test your ability to identify the most appropriate starting point for studying LLM families and understanding the techniques and datasets crucial for LLM improvement.',
    step_wise_goal_title: [
      'Understanding which LLM family to analyze first for foundational knowledge'
    ],
    step_wise_analysis: [
      "You were asked which LLM family a student should analyze first to gain foundational understanding of prominent large language model families (GPT, LLaMA, and PaLM) and their contribution to AGI development. You selected 'Beginnt mit einer detaillierten Analyse der Rolle von LLaMA in der Sprachverarbeitung aufgrund seiner einzigartigen Implementierungsmerkmale' (Begin with a detailed analysis of LLaMA's role in language processing due to its unique implementation features). However, the correct answer was 'GPT'. While LLaMA does have unique features, GPT would be the more appropriate starting point for foundational understanding because it was the pioneering model that established many fundamental concepts in large language models and has the longest development history, making it ideal for building basic comprehension before moving to more specialized variants like LLaMA."
    ],
    summary:
      "You demonstrated some understanding of LLM families by recognizing LLaMA's unique implementation characteristics. However, you missed the strategic approach to learning about LLMs systematically. Your choice shows knowledge of LLaMA's technical aspects but lacks the pedagogical insight that GPT, being the foundational and most widely studied model, would provide better groundwork for understanding the field. To improve, focus on understanding the historical development and foundational role of different LLM families, particularly how GPT established key concepts that later models like LLaMA built upon.",
    lesson_recommendations: {
      recommendations_available: true,
      identified_weaknesses: [
        'Understanding the foundational role of GPT in LLM development',
        'Comparative analysis of LLM families: GPT, LLaMA, and PaLM',
        'Techniques for fine-tuning LLMs',
        'Selection and impact of datasets on LLM performance',
        'Evaluation metrics for assessing LLM capabilities'
      ],
      recommendations: {},
      total_weaknesses: 5,
      lessons_recommendation: [],
      lesson_ids: [],
      lesson_summaries: [],
      searched_languages: ['en'],
      available_languages: ['en']
    },
    recommendations_included: true,
    document_recommendations: {
      available: true,
      documents: [
        {
          filename: 'orgs/96/db75f269-d7ae-4907-a078-82f2d728648d.csv',
          document_id: 'orgs/96/db75f269-d7ae-4907-a078-82f2d728648d.csv',
          document_title: 'orgs/96/db75f269-d7ae-4907-a078-82f2d728648d.csv',
          file_path:
            '/documents/orgs/96/db75f269-d7ae-4907-a078-82f2d728648d.csv',
          language: 'en',
          index: 'org_113_en',
          max_score: 1,
          total_chunks: 0,
          relevance_score: 0.8757,
          matched_chunks: 55,
          avg_chunk_score: 0.5857,
          ai_relevance_score: 0.9,
          ai_reason:
            'Comprehensive coverage of LLMs, including foundational role of GPT, comparative analysis of LLM families, and techniques for fine-tuning. Provides deep, actionable content relevant to the assessment goal.',
          combined_score: 0.5575140000000001,
          path: 'orgs/96/db75f269-d7ae-4907-a078-82f2d728648d.pdf',
          file_title: 'llms_survey.pdf',
          original_filename: 'orgs/96/db75f269-d7ae-4907-a078-82f2d728648d.csv',
          media_id: null
        },
        {
          filename:
            'orgs/113/AI Courses/8f5cca35-caed-4f9b-a464-8fa38ddc47f2.csv',
          document_id:
            'orgs/113/AI Courses/8f5cca35-caed-4f9b-a464-8fa38ddc47f2.csv',
          document_title:
            'orgs/113/AI Courses/8f5cca35-caed-4f9b-a464-8fa38ddc47f2.csv',
          file_path:
            '/documents/orgs/113/AI Courses/8f5cca35-caed-4f9b-a464-8fa38ddc47f2.csv',
          language: 'en',
          index: 'org_113_en',
          max_score: 0.6381495309278657,
          total_chunks: 0,
          relevance_score: 0.6604,
          matched_chunks: 12,
          avg_chunk_score: 0.471,
          ai_relevance_score: 0.7,
          ai_reason:
            'Good coverage of LLMs with moderate depth, addressing foundational concepts and practical applications. Supports the assessment goal by touching on several learning gaps.',
          combined_score: 0.433208,
          path: 'orgs/113/AI Courses/8f5cca35-caed-4f9b-a464-8fa38ddc47f2.pdf',
          file_title: 'ChatGPT for Managers.pdf',
          original_filename:
            'orgs/113/AI Courses/8f5cca35-caed-4f9b-a464-8fa38ddc47f2.csv',
          media_id: 79760
        }
      ],
      total_documents: 2,
      search_query:
        '"foundational role of GPT in LLM development AND comparative analysis of LLM families (GPT, LLaMA, PaLM) AND techniques for fine-tuning LLMs AND selection and impact of datasets on LLM performance AND evaluation metrics for assessing LLM capabilities AND practical applications of large language models AND frameworks for developing general-purpose AI agents"',
      searched_languages: ['en'],
      available_languages: ['en']
    },
    course_cover_information: 'e2fc9e5f-3a9c-442a-ab01-459c5502f7a7.jpeg',
    skill_cover_information: 'c5e93a12-04df-493b-84c0-51577a8ff4cb.jpeg',
    course_cover_full: {
      filename: '3view_course_e2fc9e5f.jpeg',
      format: 'IMAGE',
      key: 'e2fc9e5f-3a9c-442a-ab01-459c5502f7a7.jpeg',
      parent_id: null,
      type: 'image/jpeg',
      image_type: 'course'
    },
    skill_cover_full: {
      filename: '3view_skill_c5e93a12.jpeg',
      format: 'IMAGE',
      key: 'c5e93a12-04df-493b-84c0-51577a8ff4cb.jpeg',
      parent_id: null,
      type: 'image/jpeg',
      image_type: 'skill'
    },
    skill_title: 'Recommended Skill Journey for 3view',
    course_title: 'Recommended Course for 3view',
    skill_id: null,
    skill_creation_success: true,
    recommendation_metadata: {
      has_lessons: true,
      has_documents: true,
      user_language: 'en',
      multi_index_enabled: false
    }
  }

  const [isModalVisible, setIsModalVisible] = useState(false)
  const [activeSelection, setActiveSelection] = useState(null)

  const [loading, setLoading] = useState(false)
  const [isDownloading, setIsDownloading] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [response, setResponse] = useState(null)
  const [shlToken, setShlToken] = useState(null)
  const [error, setError] = useState(null)
  const [aiError, setAiError] = useState(null)
  const [smoothedProgress, setSmoothedProgress] = useState(0)
  const [isGraphView, setIsGraphView] = useState(false)

  const contentRef = useRef()
  const intervalRef = useRef(null)

  const isMounted = useRef(true) // Track mounted state
  const dispatch = useDispatch()
  const library = useSelector(state => state.library)

  const { t } = useTranslation()
  useEffect(() => {
    if (loading) {
      setIsGraphView(true)
    }
  }, [loading])

  useEffect(() => {
    if (isSaving && !library.uploadFetching) {
      setIsSaving(false)
      message.success('File saved successfully')
    }
  }, [isGraphView, library.uploadFetching])

  useEffect(() => {
    const fetchToken = async () => {
      try {
        const token = await getToken()
        setShlToken(token)
      } catch (err) {
        console.error('Error fetching SHL token:', err)
      }
    }

    fetchToken()
  }, [])

  useEffect(() => {
    console.log('Mounting UnityView, isMounted:', isMounted.current)
    return () => {
      console.log('Unmounting UnityView, isMounted:', isMounted.current)
      isMounted.current = false // Set after checking unload
      console.log('Attempting to unload Unity')
      unload()
        .then(() => console.log('Unity unloaded successfully.'))
        .catch(error => console.error('Failed to unload Unity:', error))
    }
  }, [unload])

  useEffect(() => {
    intervalRef.current = setInterval(() => {
      setSmoothedProgress(prev => {
        const newProgress = Math.min(
          prev + (loadingProgression - prev) * 0.1,
          1
        )
        console.log('NewProgress: ', newProgress.toFixed(2))
        if (isLoaded) {
          clearInterval(intervalRef.current)
        }
        if (newProgress) {
          props.isPercentageLoader(newProgress.toFixed(2), isLoaded)
        }
        return newProgress
      })
    }, 50)

    return () => clearInterval(intervalRef.current)
  }, [loadingProgression, isLoaded])

  const fetchMazeSummary = async () => {
    try {
      const payload = {
        program_id: props.mazeID,
        play_id: props.playID,
        org_id: props.orgId,
        user_id: props.userId,
        user_name: props.username,
        language: props.mazeLanguage
      }

      const response = await generateMazeSummary(payload)

      console.log('summary response', response)

      setResponse(response.data)
    } catch (error) {
      console.error('Error:', error)
      setAiError('Failed to fetch data. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const sendMessageToUnity = (gameObject, method, parameter) => {
    try {
      sendMessage(gameObject, method, parameter)
    } catch (error) {
      console.error(`Error sending message to Unity: ${error}`)
      setError(`Error sending message to Unity: ${error}`)
    }
  }

  const handleSummary = useCallback(
    async status => {
      setActiveSelection(status)
      setIsModalVisible(true)

      // if (!response && !loading) {
      setLoading(true)
      fetchMazeSummary()
      // }
    },
    [loading, response]
  )

  const handleSkillJourney = () => {
    const skillJourneyId = response.skill_id

    if (!skillJourneyId) {
      message.error('Skill journey ID not found')
      return
    }
    window.open(`/skill-journey/${skillJourneyId}`, '_blank')
  }

  useEffect(() => {
    const handleShowSummary = () => handleSummary('SUMMARY')
    const handleShowCourse = () =>
      //  handleSkillJourney()
      handleSummary('COURSE')

    addEventListener('ShowSummary', handleShowSummary)
    addEventListener('ShowCourse', handleShowCourse)

    return () => {
      removeEventListener('ShowSummary', handleShowSummary)
      removeEventListener('ShowCourse', handleShowCourse)
    }
  }, [addEventListener, removeEventListener, handleSummary])

  const handleOk = () => {
    setIsModalVisible(false)
  }

  const handleCancel = () => {
    setIsModalVisible(false)
  }

  useEffect(() => {
    if (isGraphView) {
      return
    }
    if (props.activateTTS) {
      console.log('props.activateTTS: ', props.activateTTS)
      sendMessageToUnity('_ManageJSON', 'LoadUnity', '')
    }
    sendMessageToUnity('_GetTokenAndUserID', 'ReceiveURL', props.baseURL)
    sendMessageToUnity('_GetTokenAndUserID', 'ReceiveToken', props.token)
    sendMessageToUnity('_GetTokenAndUserID', 'ReceiveUserId', props.mazeID)

    const avatarConfigDev = {
      48683: {
        assetBundleUrl:
          'https://mj-scorm-bucket.s3-accelerate.amazonaws.com/urban_man_v6_unity2022',
        avatarGender: 'male'
      },
      48682: {
        assetBundleUrl:
          'https://mj-scorm-bucket.s3-accelerate.amazonaws.com/femaleofficecharacter_v6_unity2022',
        avatarGender: 'female'
      },
      66426: {
        assetBundleUrl:
          'https://mj-scorm-bucket.s3-accelerate.amazonaws.com/Maze3DTalkingAvatar/dummymodel_v5_abv1_unity2022',
        avatarGender: 'male'
      },
      66424: {
        assetBundleUrl:
          'https://mj-scorm-bucket.s3-accelerate.amazonaws.com/Maze3DTalkingAvatar/kevin_v2_6',
        avatarGender: 'male'
      },
      66422: {
        assetBundleUrl:
          'https://mj-scorm-bucket.s3-accelerate.amazonaws.com/Maze3DTalkingAvatar/zandile_v4',
        avatarGender: 'female'
      },
      66421: {
        assetBundleUrl:
          'https://mj-scorm-bucket.s3-accelerate.amazonaws.com/Maze3DTalkingAvatar/jerry_v2',
        avatarGender: 'male'
      }
    }

    const defaultConfigDev = {
      assetBundleUrl:
        'https://mj-scorm-bucket.s3-accelerate.amazonaws.com/urban_man_v6_unity2022',
      avatarGender: 'male'
    }

    const avatarConfigProd = {
      1238494: {
        assetBundleUrl:
          'https://mj-scorm-bucket.s3-accelerate.amazonaws.com/urban_man_v6_unity2022',
        avatarGender: 'male'
      },
      1238493: {
        assetBundleUrl:
          'https://mj-scorm-bucket.s3-accelerate.amazonaws.com/femaleofficecharacter_v6_unity2022',
        avatarGender: 'female'
      },
      1410030: {
        assetBundleUrl:
          'https://mj-scorm-bucket.s3-accelerate.amazonaws.com/Maze3DTalkingAvatar/jerry_v2',
        avatarGender: 'male'
      },
      1410032: {
        assetBundleUrl:
          'https://mj-scorm-bucket.s3-accelerate.amazonaws.com/Maze3DTalkingAvatar/zandile_v4',
        avatarGender: 'female'
      },
      1410031: {
        assetBundleUrl:
          'https://mj-scorm-bucket.s3-accelerate.amazonaws.com/Maze3DTalkingAvatar/kevin_v2_6',
        avatarGender: 'male'
      },
      1410029: {
        assetBundleUrl:
          'https://mj-scorm-bucket.s3-accelerate.amazonaws.com/Maze3DTalkingAvatar/dummymodel_v5_abv1_unity2022',
        avatarGender: 'male'
      }
    }

    const defaultConfigProd = {
      assetBundleUrl:
        'https://mj-scorm-bucket.s3-accelerate.amazonaws.com/urban_man_v6_unity2022',
      avatarGender: 'male'
    }

    // rose = https://mj-scorm-bucket.s3.us-east-1.amazonaws.com/Maze3DTalkingAvatar/rose_v1

    const avatarConfig =
      process.env.NODE_ENV === 'development'
        ? avatarConfigDev
        : avatarConfigProd
    const defaultConfig =
      process.env.NODE_ENV === 'development'
        ? defaultConfigDev
        : defaultConfigProd

    const { assetBundleUrl, avatarGender } =
      avatarConfig[props.avatarId] || defaultConfig
    const avatarAccent = props.avatarAccent === null ? '' : props.avatarAccent
    const avatarVoiceType =
      props.avatarVoiceType === null ? '' : props.avatarVoiceType

    sendMessageToUnity(
      '_GetTokenAndUserID',
      'ReceiveVoiceId',
      props.avatarVoice
    )
    sendMessageToUnity('_GetTokenAndUserID', 'ReceiveGender', avatarGender)
    sendMessageToUnity(
      '_GetTokenAndUserID',
      'ReceiveAccent',
      avatarVoiceType === 'traditional' ? '' : avatarAccent
    )
    sendMessageToUnity(
      '_GetTokenAndUserID',
      'RecieveVoiceType',
      props.avatarVoiceType === null ? 'traditional' : props.avatarVoiceType
    )
    sendMessageToUnity(
      '_GetTokenAndUserID',
      'ReceiveAssetBundle',
      assetBundleUrl
    )
  }, [props])

  useEffect(() => {
    if (isLoaded) {
      props.isBuildLoadedFunc(unityProvider)
    }

    // const progressionPercentage = loadingProgression * 100;
    // props.isPercentageLoader(progressionPercentage);

    if (unityError) {
      console.error('Unity error: ', unityError)
      setError(unityError)
    }
  }, [isLoaded, unityError, loadingProgression])

  const generateSummaryPdf = () => {
    const pdf = new jsPDF('p', 'mm', 'a4')
    const pageWidth = pdf.internal.pageSize.getWidth()
    const pageHeight = pdf.internal.pageSize.getHeight()
    const margin = 20
    const maxWidth = pageWidth - margin * 2
    let currentY = margin

    const addTextWithPageBreak = (
      text,
      fontSize = 12,
      fontStyle = 'normal',
      color = [0, 0, 0]
    ) => {
      pdf.setFontSize(fontSize)
      pdf.setFont('helvetica', fontStyle)
      pdf.setTextColor(color[0], color[1], color[2])

      const lines = pdf.splitTextToSize(text, maxWidth)

      lines.forEach(line => {
        if (currentY > pageHeight - margin - 10) {
          pdf.addPage()
          currentY = margin
        }

        pdf.text(line, margin, currentY)
        currentY += fontSize * 0.5
      })

      currentY += 5
    }

    const addHeading = (text, level = 1) => {
      const fontSize = level === 1 ? 18 : level === 2 ? 16 : 14
      const fontStyle = 'bold'

      if (currentY > margin + 10) {
        currentY += 10
      }

      addTextWithPageBreak(text, fontSize, fontStyle)
    }

    pdf.setFontSize(42)
    pdf.setFont('helvetica', 'bold')
    pdf.setTextColor(0, 0, 0)

    const mainTitle = t('v5:maze_summary')
    const mainTitleWidth = pdf.getTextWidth(mainTitle)
    const mainX = (pageWidth - mainTitleWidth) / 2
    const mainY = pageHeight / 2 - 10

    pdf.text(mainTitle, mainX, mainY)

    pdf.setFontSize(14)
    pdf.setFont('helvetica', 'normal')
    const currentDate = new Date().toLocaleDateString()
    const subtitle = `Generated on ${currentDate}`
    const subtitleWidth = pdf.getTextWidth(subtitle)
    const subX = (pageWidth - subtitleWidth) / 2
    const subY = mainY + 20

    pdf.text(subtitle, subX, subY)

    pdf.setLineWidth(0.5)
    pdf.line(40, mainY + 10, pageWidth - 40, mainY + 10)

    pdf.addPage()
    currentY = margin

    if (response) {
      if (response.overview) {
        addHeading('Overview', 1)
        addTextWithPageBreak(response.overview)
      }

      if (response.step_wise_goal_title && response.step_wise_analysis) {
        addHeading('Step-wise Analysis', 1)

        response.step_wise_goal_title.forEach((title, index) => {
          if (response.step_wise_analysis[index]) {
            addHeading(`Step ${index + 1}: ${title}`, 2)
            addTextWithPageBreak(response.step_wise_analysis[index])
          }
        })
      }

      if (
        response.lesson_recommendations &&
        response.lesson_recommendations.lesson_summaries &&
        response.lesson_recommendations.lesson_summaries.length > 0
      ) {
        addHeading('Lesson-based Summaries', 1)

        response.lesson_recommendations.lesson_summaries.forEach(
          (summary, index) => {
            const titleMatch = summary.match(/^Lesson Title = (.+?)(?:\n|$)/)
            const lessonTitle = titleMatch
              ? titleMatch[1]
              : `Lesson ${index + 1}`
            const lessonContent = titleMatch
              ? summary.replace(/^Lesson Title = .+?\n/, '')
              : summary

            addHeading(`Lesson ${index + 1}: ${lessonTitle}`, 2)
            addTextWithPageBreak(lessonContent)
          }
        )
      }

      if (response.summary) {
        addHeading(t('v4:summary'), 1)
        addTextWithPageBreak(response.summary)
      }

      if (response.course_id) {
        addHeading(t('v5:course_recommendation'), 1)
        addTextWithPageBreak(t('v5:a_recommended_course_is_available'))
      }
    }

    return pdf
  }

  const handleDownloadPdf = async () => {
    setIsDownloading(true)

    try {
      const pdf = generateSummaryPdf()
      pdf.save('maze_summary.pdf')
    } catch (error) {
      console.error('Error generating PDF:', error)
    } finally {
      setIsDownloading(false)
    }
  }

  const handleSaveToDownloadCenter = async () => {
    try {
      setIsSaving(true)
      const pdf = generateSummaryPdf()
      const blob = pdf.output('blob')
      const fileName = 'maze_summary.pdf'
      const file = new File([blob], fileName, { type: 'application/pdf' })
      dispatch(actions.library.upload(file, { category: 'maze' }))
    } catch (error) {
      console.error('Error saving PDF to Download Center:', error)
    }
  }

  console.log('library log', response)

  return (
    <>
      {error && (
        <div style={{ color: 'red', textAlign: 'center' }}>
          {t('v4:an_error_occured')} {error}
        </div>
      )}

      {/* {loadingProgression < 1 && (
        <p>Loading Unity... {Math.round(loadingProgression * 100)}%</p>
      )} */}

      <Unity
        unityProvider={unityProvider}
        style={{
          height: '100%',
          width: '100%'
        }}
      />
      <Modal
        title={
          activeSelection === 'SUMMARY'
            ? t('v5:maze_summary')
            : t('v5:recommended_skill_journey')
        }
        visible={isModalVisible}
        onOk={handleOk}
        onCancel={handleCancel}
        width={1000}
        footer={[
          activeSelection === 'SUMMARY' && (
            <Button
              key="save"
              type="default"
              onClick={handleSaveToDownloadCenter}
              disabled={loading}
            >
              {library.uploadFetching ? <LoadingOutlined /> : <SaveOutlined />}{' '}
              {t('v5:downlad_center_save_summary')}
            </Button>
          ),
          activeSelection === 'SUMMARY' && (
            <Button
              loading={isDownloading}
              key="download"
              type="primary"
              onClick={handleDownloadPdf}
              disabled={loading}
            >
              <DownloadOutlined /> {t('v4:download_summary')}
            </Button>
          ),
          <Button key="ok" type="default" onClick={handleOk}>
            {t('v4:ok')}
          </Button>
        ]}
        centered
      >
        {loading && (
          <div style={{ textAlign: 'center', marginTop: '50px' }}>
            <Spin
              size="large"
              tip={
                activeSelection === 'SUMMARY'
                  ? t('v5:loading_maze_summary')
                  : 'Generating Skill Journey Recommendation...'
              }
            />
          </div>
        )}
        {response && activeSelection && activeSelection === 'SUMMARY' && (
          <div ref={contentRef} className="container-unity">
            <div className="section-unity overview">
              <h2>{t('reports:overview')}</h2>
              <p>{response.overview}</p>
            </div>

            {/* Step-wise Analysis */}
            <div className="section-unity step_wise_analysis">
              <h2>{t('v4:step_wise_analysis') || 'Step-wise Analysis'}</h2>

              {response.step_wise_goal_title.map((title, index) => (
                <div className="step-analysis" key={index}>
                  <h3>
                    {t('v4:step')} {index + 1}: {title}
                  </h3>
                  <p>{response.step_wise_analysis[index]}</p>
                </div>
              ))}
            </div>

            {/* Lesson-based summaries section */}
            {response.lesson_recommendations?.lesson_summaries?.length > 0 && (
              <div className="section-unity lesson-summaries">
                <h2>{'Lesson-based Summaries'}</h2>

                {response.lesson_recommendations?.lesson_summaries && (
                  <div className="lessons-container">
                    {response.lesson_recommendations?.lesson_summaries?.map(
                      (summary, index) => {
                        // Extract lesson title from summary if it follows the pattern "Lesson Title = ..."
                        const titleMatch = summary.match(
                          /^Lesson Title = (.+?)(?:\n|$)/
                        )
                        const lessonTitle = titleMatch
                          ? titleMatch[1]
                          : `${t('v4:lesson') || 'Lesson'} ${index + 1}`
                        const lessonContent = titleMatch
                          ? summary.replace(/^Lesson Title = .+?\n/, '')
                          : summary

                        return (
                          <div className="lesson-card" key={index}>
                            <div className="lesson-header">
                              <h3>
                                {t('general:lesson')} {index + 1}: {lessonTitle}
                              </h3>
                            </div>

                            <div className="lesson-content">
                              <p>{lessonContent}</p>
                            </div>
                          </div>
                        )
                      }
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Keep the summary section */}
            <div className="section-unity summary">
              <h2>{t('v4:summary')}</h2>
              <p>{response.summary}</p>
            </div>
          </div>
        )}

        {response && activeSelection && activeSelection === 'COURSE' && (
          <div
            style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}
          >
            {(response.skill_id || response.skill_title) && (
              <div className="recommended-skill-container">
                <div className="recommended-skill">
                  <h2>
                    {response.skill_title || t('v5:recommended_skill_journey')}
                  </h2>
                  <div
                    className="recommended-skill-item"
                    onClick={handleSkillJourney}
                  >
                    <Icon type="rocket" />
                    <p>
                      {t('v5:click_to_view_skill_journey') ||
                        'View Skill Journey'}
                    </p>
                    <Icon type="right" />
                  </div>
                </div>
              </div>
            )}

            {!response.skill_id && !response.skill_title && (
              <p>{'No Skill Journey recommended.'}</p>
            )}
          </div>
        )}

        {aiError && !loading && !response && (
          <div style={{ color: 'red', textAlign: 'center' }}>{aiError}</div>
        )}
      </Modal>
    </>
  )
}

// const mapDispatchToProps = dispatch => ({
//   upload: (file, opts) => dispatch(actions.library.upload(file, opts))
// })

// export default connect(null, mapDispatchToProps)(UnityView)
export default UnityView
